import { useEffect, useRef, useState } from 'react';
import type { ChatEntry } from '../../../shared/types';

interface ChatProps {
  chat: ChatEntry[];
  onSend: (text: string) => void;
}

export default function Chat({ chat, onSend }: ChatProps) {
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.length]);

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft('');
  };

  return (
    <div className="ttt-glass rounded-2xl flex flex-col h-72 lg:h-auto lg:max-h-[420px] w-full">
      <div className="px-4 py-2.5 border-b border-white/10 text-ttt-muted text-xs uppercase tracking-wider font-semibold">
        Chat
      </div>
      <div ref={listRef} className="ttt-scroll flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-1.5 min-h-0">
        {chat.length === 0 && (
          <p className="text-ttt-muted/60 text-sm">Say hi to your opponent…</p>
        )}
        {chat.map((entry) =>
          entry.kind === 0 ? (
            <p key={entry.id} className="text-ttt-muted/80 text-xs italic">{entry.text}</p>
          ) : (
            <p key={entry.id} className="text-sm leading-snug break-words">
              <span className="text-ttt-accent font-semibold">{entry.authorName}: </span>
              <span className="text-ttt-ink">{entry.text}</span>
            </p>
          ),
        )}
      </div>
      <div className="p-3 border-t border-white/10 flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          maxLength={200}
          placeholder="Message…"
          className="flex-1 min-w-0 bg-ttt-bg/70 border border-ttt-border rounded-lg px-3 py-2 text-sm text-ttt-ink placeholder:text-ttt-muted/50 outline-none focus:border-ttt-accent transition-colors"
        />
        <button
          onClick={submit}
          disabled={!draft.trim()}
          className="rounded-lg px-3.5 py-2 bg-gradient-to-r from-ttt-accent to-ttt-accent-dark text-ttt-bg font-semibold text-sm disabled:opacity-40 hover:brightness-110 active:scale-95 transition"
        >
          Send
        </button>
      </div>
    </div>
  );
}
